import { TaskReport } from '../gql/serverTypes';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../../graphql/conflictResolution/types';

/**
 * Conflict handler for TaskReport entities.
 * Handles version conflicts during task report updates with intelligent merging.
 */
export const taskReportConflictHandler: ConflictHandler<TaskReport> = {
    entityType: 'TaskReport',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<TaskReport> {
        // Note: This is a fallback. The server should include currentState in conflict response.
        // If we need to refetch, we'd need workspaceId and taskListUnitId which we don't have here.
        // For now, throw an error - the server should always provide currentState.
        throw new Error(
            'TaskReport refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<TaskReport>): Partial<TaskReport> {
        const merged: Partial<TaskReport> = {
            // Always use current version
            version: currentState.version,
            // Preserve ID
            id: currentState.id
        };

        // Apply intended changes
        if (intendedChanges.completedAt !== undefined) {
            merged.completedAt = intendedChanges.completedAt;
        }
        if (intendedChanges.status !== undefined) {
            merged.status = intendedChanges.status;
        }
        if (intendedChanges.comment !== undefined) {
            merged.comment = intendedChanges.comment;
        }
        if (intendedChanges.images !== undefined) {
            merged.images = intendedChanges.images;
        }
        if (intendedChanges.commentImages !== undefined) {
            merged.commentImages = intendedChanges.commentImages;
        }
        if (intendedChanges.completedBy !== undefined) {
            merged.completedBy = intendedChanges.completedBy;
        }

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
